import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const FeatureCard = ({ icon, title, description, link, delay }) => {
  const cardRef = useRef(null);
  
  useEffect(() => {
    if (!cardRef.current) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('show');
          }, delay);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(cardRef.current);
    
    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [delay]);

  return (
    <div 
      ref={cardRef}
      className="feature-card bg-white rounded-2xl shadow-xl p-8 transform transition-all duration-500 opacity-0 translate-y-8 hover:shadow-2xl hover:-translate-y-2 relative overflow-hidden"
    >
      <div className="feature-icon mb-6 text-indigo-600 flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 relative z-10">
        {icon}
      </div>
      <h3 className="text-2xl font-bold text-gray-800 mb-4 relative z-10">{title}</h3>
      <p className="text-gray-600 mb-6 relative z-10">{description}</p>
      
      <Link 
        to={link} 
        className="inline-flex items-center group text-indigo-600 font-medium hover:text-indigo-800 transition-colors duration-300 relative z-10"
      >
        Detaylı Bilgi
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-5 w-5 ml-2 transform transition-transform duration-300 group-hover:translate-x-1" 
          viewBox="0 0 20 20" 
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </Link>
      
      {/* Dekoratif diyagonal çizgi */}
      <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden rounded-tr-2xl pointer-events-none">
        <div className="absolute top-0 right-0 w-40 h-2 bg-gradient-to-r from-indigo-500 to-purple-500 transform rotate-45 origin-top-right"></div>
      </div>

      {/* Kartın içindeki dekoratif ikon (kendi ikonu ile ilgili) */}
      <div className="absolute right-4 bottom-4 opacity-5 w-32 h-32 pointer-events-none">
        <img 
          src={`/${title === "Modern Müzik Odası" ? "guitar" : 
                title === "Müzik Atölyeleri" ? "piano" : "microphone"}.svg`} 
          alt="Background Icon" 
          className="w-full h-full" 
        />
      </div>
    </div>
  );
};

const HomeFeatures = () => {
  const sectionRef = useRef(null);
  
  useEffect(() => {
    if (!sectionRef.current) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.querySelector('.section-title').classList.add('show');
          entry.target.querySelector('.section-description').classList.add('show');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    observer.observe(sectionRef.current);
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-24 bg-gray-50 relative overflow-hidden"
    >
      {/* Özel arka plan desenlerini kaldırdık - bunun yerine ana sayfadaki deseni kullanıyoruz */}
      
      {/* Dekoratif arka plan elementleri - bunlar kalsın, güzel görünüyor */}
      <div className="absolute left-0 top-0 w-64 h-64 rounded-full bg-indigo-100 filter blur-3xl opacity-40 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute right-0 bottom-0 w-80 h-80 rounded-full bg-purple-100 filter blur-3xl opacity-40 translate-x-1/3 translate-y-1/3"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <h2 className="section-title text-4xl font-bold text-gray-900 mb-6 opacity-0 transform translate-y-8 transition-all duration-700">
            Müzik Tutkusunu Kampüse Taşıyoruz
          </h2>
          <p className="section-description text-xl text-gray-600 max-w-3xl mx-auto opacity-0 transform translate-y-8 transition-all duration-700 delay-200">
            IYTE Müzik Topluluğu olarak çeşitli etkinlikler ve olanaklarla öğrencilerimize müzik yapma ve dinleme fırsatları sunuyoruz.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          <FeatureCard 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            }
            title="Modern Müzik Odası"
            description="İçerisinde çeşitli enstrümanlar ve kayıt ekipmanları bulunan profesyonel müzik odamızda pratik yapabilirsiniz."
            link="/facilities"
            delay={0}
          />
          <FeatureCard 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path d="M12 14l9-5-9-5-9 5 9 5z" />
                <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
              </svg>
            }
            title="Müzik Atölyeleri"
            description="Düzenli olarak gerçekleştirilen atölyelerle enstrüman çalma ve müzik teorisi konularında kendinizi geliştirebilirsiniz."
            link="/events"
            delay={200}
          />
          <FeatureCard 
            icon={
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            }
            title="Kampüs Konserleri"
            description="Her dönem düzenlediğimiz konserlerle müziğinizi kampüs topluluğuyla paylaşma fırsatı bulabilirsiniz."
            link="/events"
            delay={400}
          />
        </div>
      </div>
      
      {/* CSS Animasyonları */}
      <style>
        {`
        .feature-card.show {
          opacity: 1;
          transform: translateY(0);
        }
        
        .section-title.show,
        .section-description.show {
          opacity: 1;
          transform: translateY(0);
        }
      `}
      </style>
    </section>
  );
};

export default HomeFeatures;